import type { Disposable } from "@/shared/contracts/lifecycle";
import { getMessages } from "@/i18n/runtime";
import { renderStatsPanel } from "./statsPanel";
import type { StatsSnapshot } from "./statsSnapshot";
import type { RecordsState } from "./types";

interface RecordsViewOptions {
  readonly container: HTMLElement;
  readonly getStatsSnapshot: () => StatsSnapshot;
}

type RecordsTabId = "stats" | "history" | "purchases";

export class RecordsView implements Disposable {
  private readonly root: HTMLDivElement;
  private readonly tabs: ReadonlyMap<RecordsTabId, HTMLButtonElement>;
  private readonly panels: ReadonlyMap<RecordsTabId, HTMLDivElement>;
  private readonly statsContent: HTMLDivElement;
  private readonly historyList: HTMLUListElement;
  private readonly historyEmpty: HTMLDivElement;
  private readonly purchasesList: HTMLUListElement;
  private readonly purchasesEmpty: HTMLDivElement;
  private readonly getStatsSnapshot: () => StatsSnapshot;
  private readonly cleanups: Array<() => void> = [];
  private activeTab: RecordsTabId = "stats";
  private statsIntervalId: ReturnType<typeof setInterval> | undefined;

  constructor(options: RecordsViewOptions) {
    const messages = getMessages();
    this.getStatsSnapshot = options.getStatsSnapshot;

    this.root = document.createElement("div");
    this.root.className = "hover-idle-records";

    const tabBar = document.createElement("div");
    tabBar.className = "hover-idle-core-tabs";
    tabBar.setAttribute("role", "tablist");
    tabBar.setAttribute("aria-label", messages.records.tabsAria);

    const tabDefs: ReadonlyArray<{
      readonly id: RecordsTabId;
      readonly label: string;
    }> = [
      { id: "stats", label: messages.records.tabs.stats },
      { id: "history", label: messages.records.tabs.history },
      { id: "purchases", label: messages.records.tabs.purchases },
    ];

    const tabs = new Map<RecordsTabId, HTMLButtonElement>();
    const panels = new Map<RecordsTabId, HTMLDivElement>();

    for (const def of tabDefs) {
      const tabButton = document.createElement("button");
      tabButton.type = "button";
      tabButton.className = "hover-idle-core-tab hover-idle-records-tab";
      tabButton.textContent = def.label;
      tabButton.dataset.tab = def.id;
      tabButton.dataset.active = def.id === this.activeTab ? "true" : "false";
      tabButton.setAttribute("role", "tab");
      tabButton.setAttribute(
        "aria-selected",
        def.id === this.activeTab ? "true" : "false",
      );

      const handler = (): void => this.switchTab(def.id);
      this.addTabIntentListeners(tabButton, handler);
      this.cleanups.push(() => {
        this.removeTabIntentListeners(tabButton, handler);
      });

      tabBar.append(tabButton);
      tabs.set(def.id, tabButton);
    }

    this.tabs = tabs;

    const statsPanel = document.createElement("div");
    statsPanel.className = "hover-idle-records-panel";
    statsPanel.dataset.tab = "stats";
    statsPanel.setAttribute("role", "tabpanel");
    this.statsContent = document.createElement("div");
    this.statsContent.className = "hover-idle-stats-content";
    statsPanel.append(
      this.createTabSection(
        "stats",
        messages.records.tabs.stats,
        this.statsContent,
      ),
    );
    panels.set("stats", statsPanel);

    const historyPanel = document.createElement("div");
    historyPanel.className = "hover-idle-records-panel";
    historyPanel.dataset.tab = "history";
    historyPanel.setAttribute("role", "tabpanel");
    historyPanel.hidden = true;
    this.historyList = document.createElement("ul");
    this.historyList.className = "hover-idle-records-list";
    this.historyEmpty = document.createElement("div");
    this.historyEmpty.className = "hover-idle-records-empty";
    this.historyEmpty.textContent = messages.records.historyEmpty;
    historyPanel.append(
      this.createTabSection(
        "history",
        messages.records.tabs.history,
        this.historyEmpty,
        this.historyList,
      ),
    );
    panels.set("history", historyPanel);

    const purchasesPanel = document.createElement("div");
    purchasesPanel.className = "hover-idle-records-panel";
    purchasesPanel.dataset.tab = "purchases";
    purchasesPanel.setAttribute("role", "tabpanel");
    purchasesPanel.hidden = true;
    this.purchasesList = document.createElement("ul");
    this.purchasesList.className = "hover-idle-records-list";
    this.purchasesEmpty = document.createElement("div");
    this.purchasesEmpty.className = "hover-idle-records-empty";
    this.purchasesEmpty.textContent = messages.records.purchasesEmpty;
    purchasesPanel.append(
      this.createTabSection(
        "purchases",
        messages.records.tabs.purchases,
        this.purchasesEmpty,
        this.purchasesList,
      ),
    );
    panels.set("purchases", purchasesPanel);

    this.panels = panels;

    this.root.append(tabBar, statsPanel, historyPanel, purchasesPanel);
    options.container.replaceChildren(this.root);
  }

  update(snapshot: RecordsState): void {
    const messages = getMessages();
    const productNames = messages.records.productNames as Readonly<
      Record<string, string>
    >;

    const purchases = [...snapshot.purchaseHistory].sort(
      (a, b) => b.timestamp - a.timestamp,
    );
    this.purchasesList.replaceChildren();
    if (purchases.length === 0) {
      this.purchasesEmpty.hidden = false;
    } else {
      this.purchasesEmpty.hidden = true;
      for (const entry of purchases) {
        const li = document.createElement("li");
        li.className = "hover-idle-records-item";
        li.dataset.kind = "purchase";
        const label =
          productNames[entry.productId] ??
          messages.records.unknownProduct(entry.productId);
        li.append(
          createRecordItemPrimary(label),
          createRecordItemMeta(formatDate(entry.timestamp)),
        );
        this.purchasesList.append(li);
      }
    }

    const transactions = [...snapshot.tokenTransactions].sort(
      (a, b) => b.timestamp - a.timestamp,
    );
    this.historyList.replaceChildren();
    if (transactions.length === 0) {
      this.historyEmpty.hidden = false;
    } else {
      this.historyEmpty.hidden = true;
      for (const entry of transactions) {
        const li = document.createElement("li");
        li.className = "hover-idle-records-item";
        li.dataset.kind = "transaction";
        const productLabel =
          entry.productId !== undefined
            ? (productNames[entry.productId] ??
              messages.records.unknownProduct(entry.productId))
            : "";
        const amount = String(Math.abs(entry.delta));
        const delta =
          entry.delta >= 0
            ? messages.records.tokenDeltaPositive(amount)
            : messages.records.tokenDeltaNegative(amount);
        li.append(
          createRecordItemPrimary(
            delta,
            entry.delta >= 0 ? "positive" : "negative",
          ),
          ...(productLabel.length > 0
            ? [createRecordItemDetail(productLabel)]
            : []),
          createRecordItemMeta(formatDate(entry.timestamp)),
        );
        this.historyList.append(li);
      }
    }
  }

  notifyShown(): void {
    if (this.activeTab === "stats") {
      this.refreshStats();
      this.startStatsPolling();
    }
  }

  notifyHidden(): void {
    this.stopStatsPolling();
  }

  dispose(): void {
    this.stopStatsPolling();
    for (const cleanup of this.cleanups) cleanup();
    this.cleanups.length = 0;
    this.root.remove();
  }

  private refreshStats(): void {
    renderStatsPanel(this.statsContent, this.getStatsSnapshot(), getMessages());
  }

  private startStatsPolling(): void {
    this.stopStatsPolling();
    this.statsIntervalId = setInterval(() => {
      this.refreshStats();
    }, 1000);
  }

  private stopStatsPolling(): void {
    if (this.statsIntervalId !== undefined) {
      clearInterval(this.statsIntervalId);
      this.statsIntervalId = undefined;
    }
  }

  private addTabIntentListeners(
    button: HTMLButtonElement,
    handler: () => void,
  ): void {
    button.addEventListener("pointerenter", handler);
    button.addEventListener("focus", handler);
    button.addEventListener("click", handler);
  }

  private removeTabIntentListeners(
    button: HTMLButtonElement,
    handler: () => void,
  ): void {
    button.removeEventListener("pointerenter", handler);
    button.removeEventListener("focus", handler);
    button.removeEventListener("click", handler);
  }

  private switchTab(id: RecordsTabId): void {
    if (id === this.activeTab) return;
    if (this.activeTab === "stats") {
      this.stopStatsPolling();
    }
    this.panels.get(this.activeTab)!.hidden = true;
    this.panels.get(id)!.hidden = false;
    for (const [tabId, button] of this.tabs) {
      const isActive = tabId === id;
      button.dataset.active = isActive ? "true" : "false";
      button.setAttribute("aria-selected", isActive ? "true" : "false");
    }
    this.activeTab = id;
    if (id === "stats") {
      this.refreshStats();
      this.startStatsPolling();
    }
  }

  private createTabSection(
    tabId: RecordsTabId,
    title: string,
    ...children: HTMLElement[]
  ): HTMLDivElement {
    const section = document.createElement("div");
    section.className = "hover-idle-records-section";
    section.dataset.tab = tabId;

    const header = document.createElement("div");
    header.className = "hover-idle-records-section-header";

    const heading = document.createElement("h2");
    heading.className = "hover-idle-records-section-title";
    heading.textContent = title;

    header.append(heading);
    section.append(header, ...children);
    return section;
  }
}

function formatDate(timestamp: number): string {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "";
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function createRecordItemPrimary(
  text: string,
  tone?: "positive" | "negative",
): HTMLDivElement {
  const element = document.createElement("div");
  element.className = "hover-idle-records-item-primary";
  if (tone !== undefined) {
    element.dataset.tone = tone;
  }
  element.textContent = text;
  return element;
}

function createRecordItemDetail(text: string): HTMLDivElement {
  const element = document.createElement("div");
  element.className = "hover-idle-records-item-detail";
  element.textContent = text;
  return element;
}

function createRecordItemMeta(text: string): HTMLDivElement {
  const element = document.createElement("div");
  element.className = "hover-idle-records-item-meta";
  element.textContent = text;
  return element;
}
